// AI Defect Rate detail (headline metric #4) — real chains from the defects
// table (docs/plans/ai-defect-rate.md).
//
// "Of the AI-assisted PRs merged in this window, what share led to a confirmed
// defect — a closed Jira Bug whose fix removed that PR's lines — within W
// days?" The unit of analysis is the merged PR; the defect is a real ticket,
// traced by the worker's defect-trace job (line-level last-touch, with an LLM
// fallback that is labeled as such).
//
// W (`lookback`) gates the RATE only: the bugs list and the PR table show
// every chained defect regardless of latency, so a slow-burn bug is always
// visible even when it no longer moves the windowed trend.
//
// Five parts, top to bottom:
//   1. KPI strip — rate, defect count, % AI-introduced, median fix time, and
//      the honesty number (bugs the pipeline could NOT chain).
//   2. Trend — rate per origin-merge cohort, hatched while younger than W.
//   3. Breakdown — the same rate split by harness/complexity/team/repo.
//   4. Bugs — the interactive list; a row opens the defect drawer (?bug=…,
//      the same URL pattern as the session drawer).
//   5. PRs that led to defects — the static origin-PR table.
import { state, $, esc, num, usd, get, autoBucket, scopeQs, CX_LABELS, complexityLabel, SR_PALETTE } from '../core'
import { paintKpis } from '../kpis'
import { syncHash } from '../router'
import { openDetail } from '../sessions'

// Latest payload, kept so the legend/table can repaint without a refetch.
var drData: any = null
var drReqId = 0

// The metric is a bad-when-high one, so it gets the warning hue rather than the
// green every other metric section uses for its primary series.
var DR_RED = '#b4452f'
var DR_GREY = '#ece7dc'

var DIMS = [
  ['', 'none'],
  ['harness', 'Agent / harness'],
  ['complexity', 'Complexity'],
  ['team', 'Team'],
  ['repo', 'Repository'],
]

var LOOKBACKS = [7, 14, 30, 90]

// The LLM's root-cause taxonomy (defect-trace.ts) → display labels.
var CAT_LABELS: Record<string, string> = {
  logic: 'Logic error',
  'missing-validation': 'Missing validation',
  race: 'Race condition',
  config: 'Configuration',
  integration: 'Integration',
  other: 'Other',
  uncategorized: 'Uncategorized',
}

function drWinLabel() {
  return state.days === 'all' ? 'all time' : 'last ' + state.days + ' days'
}

function pct(r) {
  return r == null ? '—' : (r * 100).toFixed(1) + '%'
}

function dayOnly(iso) {
  return iso ? String(iso).slice(0, 10) : '—'
}

// Series colour by position. Both charts consume the SAME worst-first order the
// server sends, so a value keeps one colour across the trend and the breakdown —
// colour means "which value" and nothing else.
function drColor(i) { return SR_PALETTE[i % SR_PALETTE.length] }

// Dimension values are raw keys for complexity ('xl') and human already for the
// rest, so one helper keeps the legend, tooltips and bar labels in agreement.
function drSeriesLabel(label) {
  return state.dr.by === 'complexity' ? (CX_LABELS[label] || label) : label
}

export function renderDefectRate() {
  var win = esc(drWinLabel())
  $('#metric-detail').innerHTML =
    '<div class="dr-controls" id="dr-controls"></div>' +
    '<div class="dr-stats" id="dr-stats"></div>' +
    '<div class="panel">' +
      '<div class="panel-head">' +
        '<h2>Defect rate over time <span class="metric-sub">' + win +
          ' &middot; PRs dated at merge, defects counted within W</span></h2>' +
        '<span style="display:inline-flex;align-items:center;gap:8px"><span class="sr-lbl">Bucket</span>' +
          '<span class="seg" id="dr-bucket"></span></span>' +
      '</div>' +
      '<div id="dr-trend"></div>' +
      '<div class="sr-legend" id="dr-trend-legend"></div>' +
      '<div class="card-note" id="dr-trend-note"></div>' +
    '</div>' +
    '<div class="panel" id="dr-bd-panel">' +
      '<div class="panel-head"><h2 id="dr-bd-title">Defect rate by dimension</h2></div>' +
      '<div id="dr-bd"></div>' +
      '<div class="card-note" id="dr-bd-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Bugs <span class="metric-sub" id="dr-bugs-sub"></span></h2></div>' +
      '<div id="dr-bugs"></div>' +
      '<div class="card-note" id="dr-bugs-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>PRs that led to defects <span class="metric-sub" id="dr-tbl-sub"></span></h2></div>' +
      '<div id="dr-tbl"></div>' +
    '</div>'
  renderDrControls()
  loadDefectRate()
}

export function renderDrControls() {
  var el = $('#dr-controls')
  if (!el) return
  var lb = LOOKBACKS.map(function (d) {
    return '<button class="' + (d === state.dr.lookback ? 'on' : '') + '" data-w="' + d + '">' + d + 'd</button>'
  }).join('') +
    // 'None' turns W off: count every linked defect however late it surfaced,
    // and stop hatching — the intuitive "have these PRs EVER bitten us?" view.
    '<button class="' + (state.dr.lookback == null ? 'on' : '') + '" data-w="none" ' +
      'title="No window — count every defect, however late it surfaced (nothing hatched)">None</button>'
  var byOpts = DIMS.map(function (o) {
    return '<option value="' + o[0] + '"' + (o[0] === state.dr.by ? ' selected' : '') + '>' + esc(o[1]) + '</option>'
  }).join('')
  el.innerHTML =
    '<div class="sr-ctrl-row">' +
      // W is the metric's defining control: it decides how long after a merge a
      // defect still counts against it. Short W catches immediate breakage; long
      // W catches regressions. Read-time only — nothing reprocesses.
      '<span class="sr-by-ctrl"><span class="sr-lbl">Lookback (W)</span><span class="seg" id="dr-lb">' + lb + '</span></span>' +
      // Pushed to the right edge of the row, label inside the control like every
      // other section's breakdown select.
      '<span class="ctl-right"><label class="sel-inline"><span>Break down</span>' +
        '<select id="dr-by" class="sr-by">' + byOpts + '</select></label></span>' +
    '</div>'

  Array.prototype.forEach.call($('#dr-lb').children, function (b) {
    b.onclick = function () {
      var w = b.getAttribute('data-w')
      state.dr.lookback = w === 'none' ? null : parseInt(w, 10)
      renderDrControls()
      loadDefectRate()
    }
  })
  $('#dr-by').onchange = function () { state.dr.by = $('#dr-by').value; loadDefectRate() }
  var active = autoBucket(state.dr.bucket)
  var bk = ['day', 'week', 'month'].map(function (b) {
    return '<button class="' + (b === active ? 'on' : '') + '" data-b="' + b + '">' + b + '</button>'
  }).join('')
  var bkEl = $('#dr-bucket')
  if (bkEl) {
    bkEl.innerHTML = bk
    Array.prototype.forEach.call(bkEl.children, function (b) {
      b.onclick = function () { state.dr.bucket = b.getAttribute('data-b'); renderDrControls(); loadDefectRate() }
    })
  }
}

export function drQs() {
  return 'days=' + encodeURIComponent(String(state.days)) +
    '&lookback=' + encodeURIComponent(state.dr.lookback == null ? 'none' : String(state.dr.lookback)) +
    '&bucket=' + encodeURIComponent(autoBucket(state.dr.bucket)) +
    '&by=' + encodeURIComponent(state.dr.by || '') +
    scopeQs()
}

export function loadDefectRate() {
  var id = ++drReqId
  get('/api/defect-rate?' + drQs()).then(function (d) {
    if (id !== drReqId) return // a newer request is in flight — drop this one
    drData = d
    setDrKpi(d)
    paintKpis() // the headline tile mirrors the filters applied here
    paintDefectRate()
  })
}

// The tile's figure, published from here so the headline can't disagree with the
// section (same window, same W, same filters). Read by kpis.ts.
var drKpi: any = null
export function setDrKpi(d) { drKpi = d ? { kpi: d.kpi, lookback: state.dr.lookback } : null }
export function getDrKpi() { return drKpi }

function paintDefectRate() {
  var d = drData
  if (!d) return
  paintStats(d)
  // With a breakdown selected the trend splits into one bar per value per
  // bucket, so the "who is causing this" answer is visible over TIME and not
  // only in aggregate — a value can sit on the overall average while being the
  // whole of a bad week.
  var series = state.dr.by ? (d.series || []) : []
  $('#dr-trend').innerHTML = series.length
    ? groupedTrendChart(d.buckets, series)
    : trendChart(d.buckets, d.points)
  $('#dr-trend-legend').innerHTML =
    (series.length
      ? series.map(function (s, i) {
          return '<span class="leg"><span class="swatch" style="background:' + drColor(i) + '"></span>' +
            esc(drSeriesLabel(s.label)) + '</span>'
        }).join('')
      : '<span class="leg"><span class="swatch" style="background:' + DR_RED + '"></span>defect rate</span>') +
    // With W off nothing is hatched, so the maturing swatch would be a legend
    // entry with no matching bars — drop it.
    (state.dr.lookback == null
      ? ''
      : '<span class="leg"><span class="swatch dr-swatch-maturing"></span>still maturing &mdash; younger than the lookback window</span>')
  var cur = d.kpi.current || {}
  var lbOff = state.dr.lookback == null
  $('#dr-trend-note').innerHTML =
    esc(num(cur.num || 0)) + ' of ' + esc(num(cur.denom || 0)) + ' AI-assisted PRs merged in the ' +
    esc(drWinLabel()) +
    (lbOff ? ' led to a defect at any time - ' : ' led to a defect within ' + state.dr.lookback + ' days - ') +
    '<strong>' + esc(pct(cur.rate)) + '</strong>. Buckets anchor on the ORIGIN PR\'s merge' +
    (lbOff
      ? ', and count every linked defect regardless of how late it surfaced - nothing here is maturing.'
      : '; hatched ones are younger than the lookback window - their defects are still landing, so treat those rates as a floor.' +
        // W can only truncate defects OLDER than W, and nothing in a window shorter
        // than W can be that old — so on a short window the selector legitimately
        // changes nothing. Say so, or it reads as a dead control.
        (state.days !== 'all' && Number(state.days) <= (state.dr.lookback as number)
          ? ' The window is shorter than W, so nothing in it is old enough for the lookback to exclude &mdash; ' +
            'widen the window past ' + state.dr.lookback + ' days to see W bite.'
          : ''))

  var bdPanel = $('#dr-bd-panel')
  if (!state.dr.by) {
    // Panel stays VISIBLE with a prompt rather than disappearing: a section that
    // silently isn't there can't be discovered, and this was the one view users
    // didn't know existed.
    bdPanel.style.display = ''
    $('#dr-bd-title').innerHTML = 'Defect rate by dimension'
    $('#dr-bd').innerHTML =
      '<div class="dr-bd-prompt">Pick a <strong>Break down by</strong> dimension above to split the rate - ' +
      'by agent, complexity, team or repository. The trend above splits with it.</div>'
    $('#dr-bd-note').innerHTML = ''
  } else {
    var label = (DIMS.filter(function (o) { return o[0] === state.dr.by })[0] || ['', ''])[1]
    $('#dr-bd-title').innerHTML = 'Defect rate by ' + esc(String(label).toLowerCase()) +
      ' <span class="metric-sub">' + esc(drWinLabel()) + ' &middot; worst first</span>'
    $('#dr-bd').innerHTML = breakdownBars(d.breakdown, cur.rate)
    $('#dr-bd-note').innerHTML = state.dr.by === 'complexity'
      ? 'Complexity is the PR\'s meaningful added+removed lines, banded like the sessions filters.'
      : state.dr.by === 'harness'
        ? 'A PR sits under the agent that wrote MOST of its lines; "Not attributed" means no session claimed them.'
        : ''
  }

  paintBugs(d)

  $('#dr-tbl-sub').innerHTML = esc(drWinLabel()) + ' &middot; ' + esc(num((d.artifacts || []).length)) + ' with a linked defect'
  $('#dr-tbl').innerHTML = artifactTable(d.artifacts || [])
}

// ---------------------------------------------------------------------------
// KPI strip
// ---------------------------------------------------------------------------

function paintStats(d) {
  var el = $('#dr-stats')
  if (!el) return
  var cur = d.kpi.current || {}
  var counts = d.counts || {}
  var linked = counts.linked || 0
  var missed = (counts.unlinked || 0) + (counts.untraced || 0)
  var stats = [
    [pct(cur.rate), 'defect rate'],
    [num(linked), linked === 1 ? 'bug chained' : 'bugs chained'],
    [linked ? Math.round(((counts.aiIntroduced || 0) / linked) * 100) + '%' : '—', 'AI-introduced'],
    // Lifecycle halves, in order: how long it hid, then how fast it was fixed.
    [d.medianLatentDays != null ? d.medianLatentDays + 'd' : '—', 'median latent'],
    [d.medianTimeToFixDays != null ? d.medianTimeToFixDays + 'd' : '—', 'median time to fix'],
    // The honesty number: closed Bugs the pipeline could not chain to a PR.
    // Zero is the good reading, so it renders muted rather than alarmed.
    [num(missed), 'not chained'],
  ]
  el.innerHTML = stats.map(function (s) {
    return '<span class="dr-stat"><span class="dr-stat-v">' + esc(String(s[0])) + '</span>' +
      '<span class="dr-stat-l">' + esc(String(s[1])) + '</span></span>'
  }).join('')
}

// ---------------------------------------------------------------------------
// bugs list + defect drawer
// ---------------------------------------------------------------------------

function statusChip(b) {
  if (b.status === 'unlinked') return '<span class="dr-chip dr-chip-miss">no fix PR</span>'
  if (b.status === 'untraced') return '<span class="dr-chip dr-chip-miss">untraced</span>'
  // Just "AI-introduced" — whether the origin was line-traced or LLM-inferred is
  // spelled out in the drawer's "Introduced by" section, not crammed into the chip.
  if (b.aiIntroduced) return '<span class="dr-chip dr-chip-ai">AI-introduced</span>'
  return '<span class="dr-chip dr-chip-human">not AI</span>'
}

function catChip(category) {
  return category ? '<span class="dr-chip dr-chip-cat">' + esc(CAT_LABELS[category] || category) + '</span>' : ''
}

function paintBugs(d) {
  var bugs = d.defects || []
  $('#dr-bugs-sub').innerHTML = esc(drWinLabel()) + ' &middot; ' + esc(num(bugs.length)) +
    ' closed bug' + (bugs.length === 1 ? '' : 's')
  var counts = d.counts || {}
  $('#dr-bugs-note').innerHTML = (counts.unlinked || counts.untraced)
    ? 'Of these, ' +
      (counts.unlinked ? esc(num(counts.unlinked)) + ' had no fix PR the pipeline could resolve' : '') +
      (counts.unlinked && counts.untraced ? ' and ' : '') +
      (counts.untraced ? esc(num(counts.untraced)) + ' had a fix whose removed lines traced to no confident origin' : '') +
      ' &mdash; listed, never guessed into the rate.'
    : ''
  if (!bugs.length) {
    $('#dr-bugs').innerHTML = '<div class="empty">No closed bugs chained in this window. ' +
      'Bugs arrive via the Jira webhook when a Bug ticket closes; run the backfill to sweep history.</div>'
    return
  }
  // The origin→fix PR chain lives in the drawer now (it can carry several PRs and
  // full context); the row keeps just the story: key, title, tags, the two durations.
  // Fixed-width trailing columns so tags/latent/fix line up vertically across rows.
  $('#dr-bugs').innerHTML = '<div class="dr-buglist">' + bugs.map(function (b) {
    var title = b.ticketTitle || '(no title)'
    return '<button class="dr-bug" data-key="' + esc(b.ticketKey) + '">' +
      '<span class="dr-bug-key">' + esc(b.ticketKey) + '</span>' +
      '<span class="dr-bug-title" title="' + esc(title) + '">' + esc(title) + '</span>' +
      // Status and category are separate fixed columns so BOTH line up down the list
      // (a shared cell would shift the category whenever the status chip's width changed).
      '<span class="dr-bug-status">' + statusChip(b) + '</span>' +
      '<span class="dr-bug-cat">' + catChip(b.category) + '</span>' +
      '<span class="dr-bug-latent">' +
        (b.latentDays != null ? '<span title="days the bug lurked before it was reported">' + esc(String(b.latentDays)) + 'd latent</span>' : '') +
      '</span>' +
      '<span class="dr-bug-fix">' +
        (b.timeToFixDays != null ? '<span title="days from report to merged fix">' + esc(String(b.timeToFixDays)) + 'd to fix</span>' : '') +
      '</span>' +
      '<span class="dr-bug-go">&rsaquo;</span>' +
    '</button>'
  }).join('') + '</div>'
  Array.prototype.forEach.call(document.querySelectorAll('.dr-bug'), function (row) {
    row.onclick = function () { openBugDrawer(row.getAttribute('data-key')) }
  })
}

// The defect drawer reuses the global #drawer/#overlay the sessions view owns,
// with its own URL key (?bug=) so a bug is a shareable, reload-survivable link.
var bugReqId = 0

export function openBugDrawer(key) {
  if (!key) return
  // The bug drawer and a session drawer share #drawerBody and can never both be
  // "open" at once — clear any stacked session so state (and the URL) never
  // carries both while the bug is what's showing. This also drives the session
  // drawer's "back to bug" button, which re-enters here.
  state.open = null
  state.dr.bug = key
  syncHash() // URL first, same as openDetail — the fetch may lose a race
  var id = ++bugReqId
  $('#drawerBody').innerHTML = '<div class="empty">Loading ' + esc(key) + '&hellip;</div>'
  $('#drawer').classList.add('on')
  $('#overlay').classList.add('on')
  $('#drawer').scrollTop = 0
  get('/api/defect?key=' + encodeURIComponent(key)).then(function (r) {
    if (id !== bugReqId || state.dr.bug !== key) return
    if (!r || r.error || !r.defect) {
      $('#drawerBody').innerHTML = '<div class="empty">Unknown bug ' + esc(key) + '.</div>'
      return
    }
    $('#drawerBody').innerHTML = bugDrawerHtml(r)
    var closeBtn = $('#bugCloseBtn')
    if (closeBtn) closeBtn.onclick = closeBugDrawer
    // Session links open the session stacked OVER this bug (same drawer), not a
    // jump to the Sessions view — so the bug stays one "back" click away.
    Array.prototype.forEach.call(document.querySelectorAll('#drawerBody .drw-session[data-session]'), function (b) {
      b.onclick = function () { openSessionOverBug(b.getAttribute('data-session')) }
    })
  })
}

// Open a session in the SAME drawer, stacked over the current bug. state.dr.bug
// stays set, so openDetail renders a "back to <bug>" affordance and the URL
// carries both keys (?bug=…&session=…) — a shareable, reload-survivable stack.
export function openSessionOverBug(sessionId) {
  if (!sessionId) return
  openDetail(sessionId)
}

export function closeBugDrawer() {
  if (!state.dr.bug) return
  state.dr.bug = null
  $('#drawer').classList.remove('on')
  $('#overlay').classList.remove('on')
  syncHash({ replace: true })
}

// One side of the chain (origin or fix) as a bordered card: the PR link + title,
// a who/when meta line, and whatever footer the caller supplies (session button,
// traced-line count, candidates). `spendUsd` rides in the top-right as a quiet
// badge so the card carries its own cost; the headline figures live in the strip.
function prCard(side, spendUsd, footHtml) {
  if (!side) return '<div class="drw-pr-missing">not resolved</div>'
  var who = [side.author, side.team].filter(Boolean).map(esc).join(' &middot; ')
  var linkHtml = side.url
    ? '<a class="drw-pr-link" href="' + esc(side.url) + '" target="_blank" rel="noopener">' + esc(side.repo) + '#' + esc(String(side.number)) + ' &nearr;</a>'
    : '<span class="drw-pr-link">' + esc(side.repo) + '#' + esc(String(side.number)) + '</span>'
  return '<div class="drw-pr-card">' +
    '<div class="drw-pr-top">' + linkHtml +
      (spendUsd != null ? '<span class="drw-spend" title="block-attributed session spend">' + esc(usd(spendUsd)) + '</span>' : '') +
    '</div>' +
    '<div class="drw-pr-title">' + esc(side.title || '') + '</div>' +
    '<div class="drw-pr-meta">' + who + (who ? ' &middot; ' : '') + 'merged ' + esc(dayOnly(side.mergedAt)) + '</div>' +
    (footHtml || '') +
  '</div>'
}

// A commit-kind origin's card: the blame trace resolved a bare commit that
// belongs to no PR (initial commit, direct push). Same card furniture as a PR.
function commitCard(d, footHtml) {
  var sha = d.originCommitSha || ''
  var label = esc(d.originRepoOwner + '/' + d.originRepoName) + '@' + esc(sha.slice(0, 7))
  var linkHtml = d.originUrl
    ? '<a class="drw-pr-link" href="' + esc(d.originUrl) + '" target="_blank" rel="noopener">' + label + ' &nearr;</a>'
    : '<span class="drw-pr-link">' + label + '</span>'
  return '<div class="drw-pr-card">' +
    '<div class="drw-pr-top">' + linkHtml + '</div>' +
    '<div class="drw-pr-title">' + esc(d.originCommitMsg || '') + '</div>' +
    '<div class="drw-pr-meta">commit outside any PR (initial commit or direct push) &middot; ' + esc(dayOnly(d.originCommitDate)) + '</div>' +
    (footHtml || '') +
  '</div>'
}

// Session opens stacked over the bug (wired in openBugDrawer), so it's a button
// with the id in a data-attr, not an anchor that navigates away.
function sessionLink(sessionId, harness) {
  if (!sessionId) return '<span class="drw-session-none">no session attributed</span>'
  return '<button type="button" class="drw-session" data-session="' + esc(sessionId) + '">' +
    esc(harness || 'agent') + ' session &rsaquo;</button>'
}

function bugDrawerHtml(r) {
  var d = r.defect
  var harness = d.originHarness ? harnessDisplay(d.originHarness) : null
  // A linked origin is a PR — or, from the blame trace, a bare commit that
  // belongs to no PR (initial commit, direct push): still a linked origin.
  var isCommitOrigin = d.status === 'linked' && d.originKind === 'commit'
  var intro = d.status === 'linked' && d.originNumber != null
    ? { repo: d.originRepoOwner + '/' + d.originRepoName, number: d.originNumber, title: d.originTitle, url: d.originUrl, mergedAt: d.originMergedAt, author: d.originAuthor, team: d.originTeam }
    : null
  var fix = d.fixNumber != null
    ? { repo: d.fixRepoOwner + '/' + d.fixRepoName, number: d.fixNumber, title: d.fixTitle, url: d.fixUrl, mergedAt: d.fixMergedAt, author: d.fixAuthor, team: d.fixTeam }
    : null
  var cross = d.status === 'linked'
    ? (d.aiIntroduced ? 'AI-introduced (' + esc(harness || '?') + ')' : 'Human-introduced') + ', ' +
      (d.fixSessionId ? 'AI-fixed' : 'human-fixed')
    : ''

  var html =
    '<div class="drawer-head">' +
      '<div class="drawer-head-top">' +
        '<h2>' +
          (r.jiraUrl
            ? '<a class="drw-key-link" href="' + esc(r.jiraUrl) + '" target="_blank" rel="noopener">' + esc(d.ticketKey) + '<span class="drw-ext">&nearr;</span></a>'
            : '<span class="drw-key-link">' + esc(d.ticketKey) + '</span>') +
          ' <span class="drw-title">' + esc(d.ticketTitle || '') + '</span></h2>' +
        '<button class="x" type="button" id="bugCloseBtn">close</button>' +
      '</div>' +
      '<div class="drw-chips">' +
        (d.ticketPriority ? '<span class="dr-chip dr-chip-cat">' + esc(d.ticketPriority) + '</span>' : '') +
        statusChip({ status: d.status, aiIntroduced: d.aiIntroduced, originSource: d.originSource }) +
        catChip(d.llmInsight && d.llmInsight.category) +
        (cross ? '<span class="drw-cross">' + cross + '</span>' : '') +
      '</div>' +
    '</div>'

  // No bug-level cost headline: the only spend we have is the whole origin/fix
  // SESSION's, and calling a feature session's spend "cost to introduce THIS bug"
  // overstates it by orders of magnitude (a 3-line defect inside a 400-line PR).
  // Cost still shows, truthfully, as the per-PR session-spend badge on each card.

  // Lifecycle: introduced → reported → fixed, with the two derived durations.
  html += '<div class="drw-sect"><h3>Timeline</h3><div class="drw-timeline">' +
    tlStep('Introduced', intro ? dayOnly(intro.mergedAt) : isCommitOrigin ? dayOnly(d.originCommitDate) : null) +
    tlArrow(d.latentDays != null ? d.latentDays + 'd lurking' : null) +
    tlStep('Reported', dayOnly(d.ticketCreatedAt)) +
    tlArrow(d.timeToFixDays != null ? d.timeToFixDays + 'd to fix' : null) +
    tlStep('Fixed', fix ? dayOnly(fix.mergedAt) : dayOnly(d.ticketClosedAt)) +
  '</div></div>'

  var introFoot = intro || isCommitOrigin
    ? '<div class="drw-pr-foot">' +
        (intro ? sessionLink(d.originSessionId, harness) : '<span class="drw-session-none">outside any PR &mdash; no session to attribute</span>') +
        '<span class="drw-traced">' + esc(num(d.tracedLines)) + ' of the fix\'s removed lines traced here' +
        (d.originSource === 'inferred' ? ' &middot; <em>origin inferred by LLM, not line-traced</em>' : '') + '</span></div>' +
        culpritHtml(d) +
        candidateList(d)
    : ''
  html += '<div class="drw-sect"><h3>Introduced by</h3>' +
    (intro
      ? prCard(intro, r.spend ? r.spend.origin : null, introFoot)
      : isCommitOrigin
        ? commitCard(d, introFoot)
        : '<div class="drw-pr-missing">' + (d.status === 'unlinked'
            ? 'No fix PR could be resolved for this ticket, so no origin was traced.'
            : 'The fix\'s removed lines traced to no confident origin' +
              (d.llmVerdict && d.llmVerdict.verdict === 'none' ? ', and the LLM found no credible candidate either' : '') + '.') +
          '</div>') +
  '</div>'

  html += '<div class="drw-sect"><h3>Fixed by</h3>' +
    (fix
      ? prCard(fix, r.spend ? r.spend.fix : null,
          '<div class="drw-pr-foot">' + sessionLink(d.fixSessionId, d.fixHarness ? harnessDisplay(d.fixHarness) : null) + '</div>' +
          fixDiffHtml(d))
      : '<div class="drw-pr-missing">No fix PR resolved.</div>') +
  '</div>'

  if (d.llmInsight || (d.llmVerdict && d.llmVerdict.rationale)) {
    // Split into labelled blocks — what happened, whether the attribution holds,
    // and how it could have been prevented — rather than one run-on paragraph.
    html += '<div class="drw-sect"><h3>Analysis</h3><div class="drw-analysis">'
    if (d.llmInsight && d.llmInsight.rootCause) {
      html += anaBlock('Summary', esc(d.llmInsight.rootCause))
    }
    if (d.llmVerdict && d.llmVerdict.rationale) {
      var vLabel = d.llmVerdict.verdict === 'confirms' ? 'Attribution confirmed'
        : d.llmVerdict.verdict === 'disputes' ? '&#9888; Attribution disputed'
        : d.llmVerdict.verdict === 'picked' ? 'Origin picked from candidates'
        : 'Attribution'
      html += anaBlock(vLabel, esc(d.llmVerdict.rationale))
    }
    if (d.llmInsight && d.llmInsight.preventability) {
      html += anaBlock('Could it have been caught?', esc(d.llmInsight.preventability))
    }
    html += '</div><div class="card-note">Generated by the analysis model &mdash; display only, never part of the rate.</div></div>'
  }

  return html
}

// One labelled paragraph inside the Analysis section. `bodyHtml` is pre-escaped.
function anaBlock(label, bodyHtml) {
  return '<div class="drw-ana-block"><span class="drw-ana-lbl">' + label + '</span>' +
    '<p class="drw-ana-body">' + bodyHtml + '</p></div>'
}

function tlStep(label, when) {
  return '<span class="drw-tl-step"><span class="drw-tl-lbl">' + esc(label) + '</span>' +
    '<span class="drw-tl-when">' + esc(when || '—') + '</span></span>'
}

function tlArrow(caption) {
  return '<span class="drw-tl-arrow">&rarr;' +
    (caption ? '<span class="drw-tl-cap">' + esc(caption) + '</span>' : '') + '</span>'
}

function selectedCandidate(d) {
  var cands = d.originCandidates || []
  for (var i = 0; i < cands.length; i++) {
    if (isSelectedCandidate(d, cands[i])) return cands[i]
  }
  return null
}

// "Where it went wrong" — the introduced-by card's evidence: the pre-fix code
// blocks pinpointed at trace time (LLM culpritLines, else derived from the
// traced lines), snapshotted with the code so this renders GitHub-free. Lines
// the fix removed that blame to this origin are highlighted. Rows written
// before culpritBlocks existed fall back to the stored context diff.
function culpritHtml(d) {
  var sel = selectedCandidate(d)
  if (!sel) return ''
  var blocks = sel.culpritBlocks || []
  if (!blocks.length) return legacyIntroducedDiff(d, sel)
  var anyTraced = false
  var body = blocks.map(function (b) {
    var seg = '<span class="dl dl-file">' + esc(b.path + ':' + b.startLine + '-' + b.endLine) + '</span>'
    if (b.why) seg += '<span class="dl dl-why">' + esc(b.why) + '</span>'
    seg += (b.lines || []).map(function (l) {
      if (l.traced) anyTraced = true
      var no = ('     ' + l.no).slice(-5)
      return '<span class="dl ' + (l.traced ? 'dl-del' : 'dl-ctx') + '">' + esc(no + '| ' + l.text) + '</span>'
    }).join('')
    return seg
  }).join('<span class="dl dl-gap"></span>')
  // Highlighted = a line the fix removed that traces to this origin. A block
  // can legitimately carry none: the flaw lived here, but the fix changed code
  // elsewhere (added a guard, handled a case) — say which reading applies.
  var legend = anyTraced
    ? 'Highlighted lines were removed by the fix and trace to this origin.'
    : 'The fix changed code elsewhere; these blocks are where the flaw lived.'
  return '<details class="drw-diff" open><summary>Where it went wrong</summary>' +
    '<pre class="drw-diff-pre">' + body + '</pre>' +
    '<div class="card-note">' + legend + '</div></details>'
}

// "The fix" — the fix PR's landed diff, stored display-rendered on the selected
// candidate by the blame trace. Only rendered for rows that carry culpritBlocks:
// on OLDER rows contextDiff held the ORIGIN's hunks and belongs to the other card.
function fixDiffHtml(d) {
  var sel = selectedCandidate(d)
  if (!sel || !sel.contextDiff || !(sel.culpritBlocks || []).length) return ''
  return '<details class="drw-diff"><summary>The fix (diff)</summary>' +
    '<pre class="drw-diff-pre">' + diffSpans(sel.contextDiff) + '</pre></details>'
}

// Pre-culpritBlocks rows: the stored diff was the LLM's evidence render — strip
// its per-line blame annotations ("   [#94 "title" …]") before display.
function legacyIntroducedDiff(d, sel) {
  var lines = sel.introducedLines || []
  var diff = sel.contextDiff
  if (!diff && lines.length) diff = lines.map(function (l) { return '+' + l.text }).join('\n')
  if (!diff) return ''
  return '<details class="drw-diff"><summary>Traced lines' + (lines.length ? ' (' + lines.length + ')' : '') +
    '</summary><pre class="drw-diff-pre">' + diffSpans(diff) + '</pre></details>'
}

// Unified-diff text → classed block spans. Joined with '' — a literal newline
// inside the <pre> would add a second (empty) line per row. Blame annotations
// and prompt furniture are stripped.
function diffSpans(diff) {
  var ANN = /\s{2,}\[(?:#\d+|commit [0-9a-f]{7,})[^\]]*\]\s*$/
  return diff.split('\n').map(function (raw) {
    var ln = raw.replace(ANN, '')
    var head2 = ln.slice(0, 2)
    var cls = head2 === '@@' ? 'hunk'
      : ln.indexOf('+++') === 0 || ln.indexOf('---') === 0 || ln.indexOf('### ') === 0 ? 'file'
      : ln.indexOf('…') === 0 || ln.indexOf('(files without evidence') === 0 ? 'hunk'
      : ln.charAt(0) === '+' ? 'add'
      : ln.charAt(0) === '-' ? 'del'
      : 'ctx'
    return '<span class="dl dl-' + cls + '">' + esc(ln) + '</span>'
  }).join('')
}

// Every candidate that held ANY evidence — what the gate (or LLM) chose between.
// Shown only when there was a real choice; a clean single-origin trace needs no
// second-guessing furniture.
function candidateList(d) {
  var cands = d.originCandidates || []
  if (cands.length < 2 && d.status === 'linked') return ''
  if (!cands.length) return ''
  return '<div class="drw-cands"><span class="drw-cands-lbl">Candidates considered:</span> ' +
    cands.map(function (c) {
      var isOrigin = isSelectedCandidate(d, c)
      // A commit-kind candidate (blame path) has no PR number — show its sha.
      var label = c.sha && !c.number ? esc(String(c.sha).slice(0, 7)) : '#' + esc(String(c.number))
      return '<span class="drw-cand' + (isOrigin ? ' on' : '') + '">' + label +
        ' (' + esc(String(c.lines)) + ' lines)</span>'
    }).join(' ') + '</div>'
}

// Does this candidate entry describe the row's selected origin? PR origins
// match on number+repo; commit origins on the blamed sha.
function isSelectedCandidate(d, c) {
  if (d.originKind === 'commit') return !!c.sha && c.sha === d.originCommitSha
  return d.originNumber === c.number && d.originRepoName === c.repoName
}

// Session sources arrive as display labels from the aggregate ('Claude Code'),
// but the drawer reads the RAW row (claude-code) — one mapping keeps them equal.
var HARNESS_DISPLAY: Record<string, string> = {
  'claude-code': 'Claude Code', codex: 'Codex', opencode: 'OpenCode', pi: 'Pi',
}
function harnessDisplay(source) {
  return HARNESS_DISPLAY[source] || source
}

// ---------------------------------------------------------------------------
// charts (single-series + grouped) — shared percent axis, hatched maturity
// ---------------------------------------------------------------------------

// Percent-axis bar chart of the rate per cohort bucket. A bar chart rather than a
// line because each bucket is an independent cohort with its own sample size, and
// a connecting line would imply a continuity that start-based cohorts don't have.
// Maturing buckets are hatched — see the panel note.
function trendChart(buckets, points) {
  if (!buckets || !buckets.length) return '<div class="empty">No PRs in range.</div>'
  if (!points || !points.some(function (p) { return p && p.denom > 0 })) {
    return '<div class="empty">No AI-assisted PRs in range.</div>'
  }
  var W = 920, H = 240, padL = 48, padR = 12, padT = 16, padB = 28
  var plotW = W - padL - padR, plotH = H - padT - padB, n = buckets.length
  var byB = {}
  points.forEach(function (p) { byB[p.bucket] = p })
  // Adaptive top rounded up to a clean 5% step: defect rates live in single
  // digits, so a fixed 0–100% axis would flatten every bar into the baseline.
  var maxR = 0
  points.forEach(function (p) { if (p.rate != null && p.rate > maxR) maxR = p.rate })
  var top = Math.max(0.05, Math.ceil((maxR * 1.15) / 0.05) * 0.05)
  var yOf = function (v) { return padT + (1 - v / top) * plotH }
  var base = yOf(0), bw = plotW / n
  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet">' +
    '<defs><pattern id="dr-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">' +
    '<rect width="5" height="5" fill="' + DR_RED + '" fill-opacity="0.18"/>' +
    '<line x1="0" y1="0" x2="0" y2="5" stroke="' + DR_RED + '" stroke-width="2"/></pattern></defs>'
  ;[0, 0.5, 1].forEach(function (g) {
    var v = top * g, y = yOf(v)
    svg += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="' + DR_GREY + '"/>'
    svg += '<text x="' + (padL - 6) + '" y="' + (y + 3) + '" text-anchor="end">' + (v * 100).toFixed(0) + '%</text>'
  })
  var step = Math.ceil(n / 9)
  buckets.forEach(function (b, i) {
    var x = padL + i * bw + 2, w = Math.max(2, bw - 4), p = byB[b]
    if (p && p.denom > 0 && p.rate != null) {
      var t = yOf(p.rate)
      var fill = p.maturing ? 'url(#dr-hatch)' : DR_RED
      svg += '<rect x="' + x + '" y="' + t.toFixed(1) + '" width="' + w + '" height="' + Math.max(0, base - t).toFixed(1) +
        '" rx="2" fill="' + fill + '"/>'
      var tip = b + ': ' + p.num + '/' + p.denom + ' defective (' + pct(p.rate) + ')' + (p.maturing ? ' · still maturing' : '')
      svg += '<rect x="' + x + '" y="' + padT + '" width="' + w + '" height="' + (base - padT) +
        '" fill="transparent"><title>' + esc(tip) + '</title></rect>'
    }
    if (i % step === 0) {
      svg += '<text x="' + (padL + i * bw + bw / 2).toFixed(1) + '" y="' + (H - padB + 14) +
        '" text-anchor="middle">' + esc(b) + '</text>'
    }
  })
  svg += '</svg>'
  return svg
}

// Breakdown mode: the same cohort axis, but one bar PER dimension value per
// bucket, each in that value's colour. Answers "who is causing this, and when" —
// a harness can sit on the overall average while being the whole of a bad week,
// which the aggregate breakdown below cannot show. Maturing bars are hatched in
// their own hue, so the caveat survives the split.
function groupedTrendChart(buckets, series) {
  if (!buckets || !buckets.length) return '<div class="empty">No PRs in range.</div>'
  var any = series.some(function (s) {
    return (s.points || []).some(function (p) { return p && p.denom > 0 })
  })
  if (!any) return '<div class="empty">No AI-assisted artifacts in range.</div>'
  var W = 920, H = 240, padL = 48, padR = 12, padT = 16, padB = 28
  var plotW = W - padL - padR, plotH = H - padT - padB, n = buckets.length
  var idx = series.map(function (s) {
    var m = {}
    ;(s.points || []).forEach(function (p) { m[p.bucket] = p })
    return m
  })
  var maxR = 0
  series.forEach(function (s) {
    (s.points || []).forEach(function (p) { if (p.rate != null && p.rate > maxR) maxR = p.rate })
  })
  var top = Math.max(0.05, Math.ceil((maxR * 1.15) / 0.05) * 0.05)
  var yOf = function (v) { return padT + (1 - v / top) * plotH }
  var base = yOf(0), bw = plotW / n, k = series.length || 1
  var gpad = Math.min(8, bw * 0.18), inner = bw - gpad, sw = Math.max(1.5, inner / k)
  // One hatch pattern per series — a pattern can't inherit a fill, so the
  // maturing treatment has to be defined per colour up front.
  var defs = series.map(function (s, i) {
    var c = drColor(i)
    return '<pattern id="dr-h' + i + '" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">' +
      '<rect width="5" height="5" fill="' + c + '" fill-opacity="0.16"/>' +
      '<line x1="0" y1="0" x2="0" y2="5" stroke="' + c + '" stroke-width="2"/></pattern>'
  }).join('')
  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet"><defs>' + defs + '</defs>'
  ;[0, 0.5, 1].forEach(function (g) {
    var v = top * g, y = yOf(v)
    svg += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="' + DR_GREY + '"/>'
    svg += '<text x="' + (padL - 6) + '" y="' + (y + 3) + '" text-anchor="end">' + (v * 100).toFixed(0) + '%</text>'
  })
  var step = Math.ceil(n / 9)
  buckets.forEach(function (b, i) {
    var gx = padL + i * bw + gpad / 2
    series.forEach(function (s, si) {
      var p = idx[si][b]
      if (!p || p.denom <= 0 || p.rate == null) return
      var x = gx + si * sw, w = Math.max(1, sw - 0.6), t = yOf(p.rate)
      var fill = p.maturing ? 'url(#dr-h' + si + ')' : drColor(si)
      // A real zero still gets a sliver, so "measured 0%" reads differently from
      // "no artifacts in this bucket" (which draws nothing at all).
      var h = Math.max(p.rate > 0 ? 1 : 0, base - t)
      svg += '<rect x="' + x.toFixed(1) + '" y="' + t.toFixed(1) + '" width="' + w.toFixed(1) +
        '" height="' + h.toFixed(1) + '" rx="1" fill="' + fill + '"/>'
      var tip = b + ' · ' + drSeriesLabel(s.label) + ': ' + p.num + '/' + p.denom + ' (' + pct(p.rate) + ')' +
        (p.maturing ? ' · still maturing' : '')
      svg += '<rect x="' + x.toFixed(1) + '" y="' + padT + '" width="' + w.toFixed(1) + '" height="' + (base - padT) +
        '" fill="transparent"><title>' + esc(tip) + '</title></rect>'
    })
    if (i % step === 0) {
      svg += '<text x="' + (padL + i * bw + bw / 2).toFixed(1) + '" y="' + (H - padB + 14) +
        '" text-anchor="middle">' + esc(b) + '</text>'
    }
  })
  svg += '</svg>'
  return svg
}

// Horizontal bars, one per dimension value, with the window's overall rate as a
// dashed reference. Horizontal because the value labels are words (model ids,
// harness names) that don't fit under a vertical bar.
function breakdownBars(rows, overall) {
  if (!rows || !rows.length) return '<div class="empty">No PRs in range.</div>'
  var maxR = rows.reduce(function (m, r) { return Math.max(m, r.rate || 0) }, 0) || 0.01
  var top = maxR * 1.25
  return '<div class="dr-bars">' + rows.map(function (r, i) {
    var label = drSeriesLabel(r.label)
    var w = ((r.rate || 0) / top) * 100
    var ref = overall != null ? (overall / top) * 100 : null
    return '<div class="dr-bar-row" title="' + esc(label + ': ' + r.num + '/' + r.denom + ' (' + pct(r.rate) + ')') + '">' +
      // Same colour this value carries in the trend above — the two charts are
      // one story, so a reader shouldn't have to re-learn the key between them.
      '<div class="dr-bar-lbl"><span class="swatch" style="background:' + drColor(i) + '"></span>' + esc(label) + '</div>' +
      '<div class="dr-bar-track">' +
        '<div class="dr-bar-fill" style="width:' + w.toFixed(1) + '%;background:' + drColor(i) + '"></div>' +
        (ref != null ? '<div class="dr-bar-ref" style="left:' + ref.toFixed(1) + '%"></div>' : '') +
      '</div>' +
      '<div class="dr-bar-val">' + esc(pct(r.rate)) + ' <span class="dr-bar-n">' + esc(r.num + '/' + r.denom) + '</span></div>' +
    '</div>'
  }).join('') + '</div>' +
  (overall != null ? '<div class="card-note">Dashed line: the window\'s overall rate (' + esc(pct(overall)) + ').</div>' : '')
}

// ---------------------------------------------------------------------------
// origin-PR table (static, last)
// ---------------------------------------------------------------------------

function artifactTable(rows) {
  if (!rows.length) {
    return '<div class="empty">No defects traced to AI-assisted PRs in this window &mdash; ' +
      'try a longer lookback or a wider window.</div>'
  }
  var head = '<thead><tr><th>PR</th><th>Author</th><th>Complexity</th><th>Harness</th>' +
    '<th>Merged</th><th>Defect</th><th class="dr-right">Days to defect</th></tr></thead>'
  var body = '<tbody>' + rows.map(function (r) {
    return '<tr>' +
      '<td><span class="dr-art-num">#' + esc(String(r.number)) + '</span> ' +
        esc(r.title || '') + '<div class="dr-art-repo">' + esc(r.repo) + '</div></td>' +
      '<td>' + esc(r.author || '—') + '<div class="dr-art-repo">' + esc(r.team || '') + '</div></td>' +
      '<td>' + esc(complexityLabel('pr', r.complexity) || CX_LABELS[r.cxBand] || '') +
        '<div class="dr-art-repo">' + esc(num(r.complexity)) + ' lines</div></td>' +
      '<td>' + esc(r.harness) + '</td>' +
      '<td>' + esc(dayOnly(r.mergedAt)) + '</td>' +
      '<td>' + catChip(r.category) + ' <span class="dr-art-repo">' + esc(r.defectRef) + '</span></td>' +
      '<td class="dr-right">' + (r.daysToDefect != null ? esc(String(r.daysToDefect)) + 'd' : '—') + '</td>' +
    '</tr>'
  }).join('') + '</tbody>'
  return '<div class="dr-tbl-wrap"><table class="dr-table">' + head + body + '</table></div>'
}
